import { Col, Row } from "antd";
import { useSelector } from "react-redux";
import { RootState } from "../../../apis/store";
import { LuPhone, LuMapPin } from "react-icons/lu";
import { HiOutlineMailOpen } from "react-icons/hi";
import { FaRegUser } from "react-icons/fa";
import { BsCalendar2Date } from "react-icons/bs";
import { FaCity, FaRegFlag } from "react-icons/fa";
import moment from "moment";
import { useTranslation } from "react-i18next";
import "moment/locale/ar";
import type { ReactNode } from "react";

function formatBirthdate(
  raw: string | undefined,
  lang: string
): string {
  if (!raw || String(raw).trim() === "") return "—";
  const m = moment(raw);
  if (!m.isValid()) return String(raw);
  return m
    .locale(lang.startsWith("ar") ? "ar" : "en")
    .format("LL");
}

interface AccountFieldProps {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  valueDir?: "ltr" | "rtl";
}

const AccountField = ({
  icon,
  label,
  value,
  valueDir,
}: AccountFieldProps) => (
  <div className="ac-account-details__field">
    <div className="ac-account-details__field-inner">
      <span className="ac-account-details__icon-wrap" aria-hidden>
        {icon}
      </span>
      <div className="ac-account-details__field-body">
        <span className="ac-account-details__label ac--font-expo">{label}</span>
        <span
          className={`ac-account-details__value ac--font-expo${
            valueDir === "ltr" ? " ac-account-details__value--ltr" : ""
          }`}
          dir={valueDir}
        >
          {value ?? "—"}
        </span>
      </div>
    </div>
  </div>
);

const AccountDetails = () => {
  const { t, i18n } = useTranslation();
  const { profil } = useSelector((state: RootState) => state?.login);
  const lang = i18n.language || "en";

  const iconProps = {
    className: "ac-account-details__icon",
    size: 20,
    "aria-hidden": true as const,
  };

  if (!profil?.data) {
    return null;
  }

  const d = profil.data;
  const fullName = [d.title, d.name].filter(Boolean).join(" ").trim();

  return (
    <div className="ac-account-details">
      <section
        className="ac-account-details__section"
        aria-labelledby="ac-account-section-profile"
      >
        <h3
          id="ac-account-section-profile"
          className="ac-account-details__section-title ac--font-expo"
        >
          {t("profile.account.sectionProfile")}
        </h3>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={12}>
            <AccountField
              icon={<FaRegUser {...iconProps} />}
              label={t("profile.account.fullname")}
              value={fullName || "—"}
            />
          </Col>
          <Col xs={24} sm={12} lg={12}>
            <AccountField
              icon={<BsCalendar2Date {...iconProps} />}
              label={t("profile.account.birthdate")}
              value={formatBirthdate(d.birthdate, lang)}
            />
          </Col>
        </Row>
      </section>

      <section
        className="ac-account-details__section"
        aria-labelledby="ac-account-section-location"
      >
        <h3
          id="ac-account-section-location"
          className="ac-account-details__section-title ac--font-expo"
        >
          {t("profile.account.sectionLocation")}
        </h3>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={8}>
            <AccountField
              icon={<FaRegFlag {...iconProps} />}
              label={t("profile.account.country")}
              value={d.country_id}
            />
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <AccountField
              icon={<FaCity {...iconProps} />}
              label={t("profile.account.city")}
              value={d.city}
            />
          </Col>
          <Col xs={24} sm={24} lg={8}>
            <AccountField
              icon={<LuMapPin {...iconProps} />}
              label={t("profile.account.street")}
              value={d.street}
            />
          </Col>
        </Row>
      </section>

      <section
        className="ac-account-details__section ac-account-details__section--last"
        aria-labelledby="ac-account-section-contact"
      >
        <h3
          id="ac-account-section-contact"
          className="ac-account-details__section-title ac--font-expo"
        >
          {t("profile.account.sectionContact")}
        </h3>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <AccountField
              icon={<LuPhone {...iconProps} />}
              label={t("profile.account.phone")}
              value={d.phone}
              valueDir="ltr"
            />
          </Col>
          <Col xs={24} sm={12}>
            <AccountField
              icon={<HiOutlineMailOpen {...iconProps} />}
              label={t("profile.account.email")}
              value={d.email}
              valueDir="ltr"
            />
          </Col>
        </Row>
      </section>
    </div>
  );
};

export default AccountDetails;
